import React, { useEffect, useState } from "react";
import dayjs from "dayjs";
import { showToast } from "../../utils/show_toast";

const EMPTY_FORM = {
    broker: "kiwoom",   // 신규 (DAS-169)
    ticker: "",
    name: "",           // 신규 (DAS-169)
    trade_date: dayjs().format("YYYY-MM-DD"),
    quantity: "", price_usd: "", amount_krw: "", usd_krw: "",
};

/**
 * PurchaseFormModal — 매수 기록 등록/수정 모달.
 * mode="create"는 POST /api/stocks/purchases (즉시 confirmed),
 * mode="edit"는 PUT /api/stocks/purchases/{id} {action:"edit"}로 확정 행을 통째로 교체한다.
 * 미래에셋은 티커당 confirmed 1행을 포지션 스냅샷으로 유지하므로 수정 모드가 갱신 경로다.
 * edit 모드는 항상 폼 전체를 전송한다(부분 페이로드 아님) — 플랜 연동 행의 ticker/trade_date/broker
 * readonly 잠금은 백엔드 병합 이후(M6)에 적용되므로 프론트는 입력만 비활성화하면 된다.
 */
function PurchaseFormModal({ open, mode = "create", initial, onClose, onSubmit, saving }) {
    const [form, setForm] = useState(EMPTY_FORM);

    useEffect(() => {
        if (open) setForm(initial ?? EMPTY_FORM);
    }, [open, initial]);

    if (!open) return null;

    const isEdit = mode === "edit";
    // 적립 플랜이 생성한 확정 행 — 식별 필드(ticker/trade_date/broker)는 플랜에 종속되어 수정 대상이 아니다 (T5 M6).
    const isPlanLocked = isEdit && initial?.plan_id != null;

    const handleSubmit = () => {
        const ticker = String(form.ticker || "").trim().toUpperCase();
        const name = String(form.name || "").trim();
        const broker = form.broker || "kiwoom";
        const quantity = Number(form.quantity);
        const priceUsd = Number(form.price_usd);
        const amountKrwRaw = String(form.amount_krw ?? "").trim();
        const usdKrwRaw = String(form.usd_krw ?? "").trim();
        const amountKrw = amountKrwRaw ? Number(form.amount_krw) : null;
        const usdKrw = usdKrwRaw ? Number(form.usd_krw) : null;

        if (!ticker) {
            showToast({ type: "error", message: "티커를 입력해주세요." });
            return;
        }
        if (!form.trade_date) {
            showToast({ type: "error", message: "체결일을 선택해주세요." });
            return;
        }
        if (!Number.isFinite(quantity) || quantity <= 0) {
            showToast({ type: "error", message: "수량은 0보다 커야 합니다." });
            return;
        }
        if (!Number.isFinite(priceUsd) || priceUsd <= 0) {
            showToast({ type: "error", message: "체결가(USD)는 0보다 커야 합니다." });
            return;
        }
        if (amountKrw != null && (!Number.isFinite(amountKrw) || amountKrw <= 0)) {
            showToast({ type: "error", message: "투입 원화는 0보다 커야 합니다." });
            return;
        }
        if (usdKrw != null && (!Number.isFinite(usdKrw) || usdKrw <= 0)) {
            showToast({ type: "error", message: "체결 환율은 0보다 커야 합니다." });
            return;
        }

        const payload = {
            broker,
            ticker,
            trade_date: form.trade_date,
            quantity,
            price_usd: priceUsd,
        };
        if (isEdit) {
            // edit은 항상 폼 전체를 전송한다 — name/amount_krw/usd_krw를 조건부로 빼면 값을
            // 지워도 백엔드 병합(R2)이 기존 값을 되살려 "지우기"가 불가능해진다.
            // 빈 문자열은 백엔드 trimmedOrNil이 NULL로 처리한다.
            payload.name = name;
            payload.amount_krw = amountKrw != null ? Math.round(amountKrw) : null;
            payload.usd_krw = usdKrw;
        } else {
            if (name) payload.name = name;
            if (amountKrw != null) payload.amount_krw = Math.round(amountKrw);
            if (usdKrw != null) payload.usd_krw = usdKrw;
        }
        onSubmit(payload);
    };

    return (
        <div
            className="fixed inset-0 z-50 flex items-center justify-center bg-overlay px-4"
            onClick={onClose}
            role="button"
            tabIndex={0}
            aria-label="닫기"
            onKeyDown={(e) => { if (e.key === "Escape") onClose(); }}
        >
            <div
                className="w-full max-w-sm rounded-2xl border border-surface-border bg-surface-card p-4 shadow-elevated sm:p-5"
                onClick={(e) => e.stopPropagation()}
            >
                <div className="mb-3 text-sm font-semibold text-content">
                    {isEdit ? "매수 기록 수정" : "수동 매수 추가"}
                </div>

                <div className="space-y-3">
                    <div>
                        <label htmlFor="stock-purchase-broker" className="mb-1 block text-xs text-content-muted">증권사</label>
                        <select
                            id="stock-purchase-broker"
                            value={form.broker}
                            disabled={isPlanLocked}
                            onChange={(e) => setForm((p) => ({ ...p, broker: e.target.value }))}
                            className="min-h-[44px] w-full rounded-lg border border-surface-border bg-surface-hover p-2 text-sm text-content outline-none focus:border-brand disabled:opacity-50"
                        >
                            <option value="kiwoom">키움증권</option>
                            <option value="mirae">미래에셋증권</option>
                        </select>
                        {form.broker === "mirae" && (
                            <div className="mt-1.5 space-y-1 text-xs text-content-muted">
                                <p>미래에셋은 티커당 1건으로 보유 총량을 관리하세요.</p>
                                <p>
                                    여러 번 나눠 매수했다면 <span className="text-content-secondary">가중평균 매입환율</span>을
                                    입력하세요. 이 값은 환차손익 분해에만 쓰이며, 총손익은 평가액과 투입 원화로
                                    계산되어 영향받지 않습니다.
                                </p>
                            </div>
                        )}
                    </div>
                    <div>
                        <label htmlFor="stock-purchase-ticker" className="mb-1 block text-xs text-content-muted">티커</label>
                        <input
                            id="stock-purchase-ticker"
                            type="text"
                            value={form.ticker}
                            readOnly={isPlanLocked}
                            onChange={(e) => setForm((p) => ({ ...p, ticker: e.target.value.toUpperCase() }))}
                            placeholder="예: SCHD"
                            className="w-full rounded-lg border border-surface-border bg-surface-hover p-2 text-sm text-content outline-none focus:border-brand read-only:opacity-50"
                        />
                    </div>
                    <div>
                        <label htmlFor="stock-purchase-name" className="mb-1 block text-xs text-content-muted">종목명 (선택)</label>
                        <input
                            id="stock-purchase-name"
                            type="text"
                            value={form.name}
                            onChange={(e) => setForm((p) => ({ ...p, name: e.target.value }))}
                            placeholder="예: Schwab US Dividend Equity ETF"
                            className="w-full rounded-lg border border-surface-border bg-surface-hover p-2 text-sm text-content outline-none focus:border-brand"
                        />
                    </div>
                    <div>
                        <label htmlFor="stock-purchase-date" className="mb-1 block text-xs text-content-muted">체결일</label>
                        <input
                            id="stock-purchase-date"
                            type="date"
                            value={form.trade_date}
                            readOnly={isPlanLocked}
                            onChange={(e) => setForm((p) => ({ ...p, trade_date: e.target.value }))}
                            className="w-full rounded-lg border border-surface-border bg-surface-hover p-2 text-sm text-content outline-none focus:border-brand read-only:opacity-50"
                        />
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label htmlFor="stock-purchase-qty" className="mb-1 block text-xs text-content-muted">수량</label>
                            <input
                                id="stock-purchase-qty"
                                type="number"
                                step="0.000001"
                                min="0"
                                value={form.quantity}
                                onChange={(e) => setForm((p) => ({ ...p, quantity: e.target.value }))}
                                placeholder="0.000000"
                                className="w-full rounded-lg border border-surface-border bg-surface-hover p-2 text-sm text-content outline-none focus:border-brand"
                            />
                        </div>
                        <div>
                            <label htmlFor="stock-purchase-price" className="mb-1 block text-xs text-content-muted">체결가 (USD)</label>
                            <input
                                id="stock-purchase-price"
                                type="number"
                                step="0.0001"
                                min="0"
                                value={form.price_usd}
                                onChange={(e) => setForm((p) => ({ ...p, price_usd: e.target.value }))}
                                placeholder="0.00"
                                className="w-full rounded-lg border border-surface-border bg-surface-hover p-2 text-sm text-content outline-none focus:border-brand"
                            />
                        </div>
                    </div>
                    <div className="grid grid-cols-2 gap-2">
                        <div>
                            <label htmlFor="stock-purchase-amount" className="mb-1 block text-xs text-content-muted">투입 원화 (선택)</label>
                            <input
                                id="stock-purchase-amount"
                                type="number"
                                min="0"
                                value={form.amount_krw}
                                onChange={(e) => setForm((p) => ({ ...p, amount_krw: e.target.value }))}
                                placeholder="예: 100000"
                                className="w-full rounded-lg border border-surface-border bg-surface-hover p-2 text-sm text-content outline-none focus:border-brand"
                            />
                        </div>
                        <div>
                            <label htmlFor="stock-purchase-fx" className="mb-1 block text-xs text-content-muted">체결 환율 (선택)</label>
                            <input
                                id="stock-purchase-fx"
                                type="number"
                                step="0.01"
                                min="0"
                                value={form.usd_krw}
                                onChange={(e) => setForm((p) => ({ ...p, usd_krw: e.target.value }))}
                                placeholder="예: 1380.50"
                                className="w-full rounded-lg border border-surface-border bg-surface-hover p-2 text-sm text-content outline-none focus:border-brand"
                            />
                        </div>
                    </div>
                </div>

                <div className="mt-4 flex justify-end gap-2">
                    <button
                        type="button"
                        onClick={onClose}
                        className="min-h-[44px] rounded-lg border border-surface-border px-3 py-1.5 text-xs text-content-secondary hover:text-content"
                    >
                        취소
                    </button>
                    <button
                        type="button"
                        onClick={handleSubmit}
                        disabled={saving}
                        className="min-h-[44px] rounded-lg bg-brand/20 border border-brand/30 px-3 py-1.5 text-xs font-medium text-brand-light hover:bg-brand/30 disabled:opacity-50"
                    >
                        {saving ? "저장 중..." : (isEdit ? "저장" : "등록")}
                    </button>
                </div>
            </div>
        </div>
    );
}

export default PurchaseFormModal;
